import { useCallback } from 'react';

import type { CandidatesManagementPageAnalytics } from '../AppPageBlock/createPageAnalytics';

const SOME_HIGH_FIT_ERROR_CODE = 'some_high_fit_applicant_not_shown';
const ALL_HIGH_FIT_ERROR_CODE = 'all_high_fit_applicant_not_shown';

type Params = {
  pageAnalytics: CandidatesManagementPageAnalytics;
};

type HighFitErrorType = 'partial-errors' | 'all-errors';
type ApplicationsFitSuccessStatus = Array<{ fitSummary: { success: boolean } }>;

export const useHighFitError = ({ pageAnalytics }: Params) => {
  const trackAlertViewed = useCallback(
    (errorType: HighFitErrorType) => {
      pageAnalytics.trackView({
        event: 'alert_viewed',
        attributes: {
          alertShownPosition: 'content',
          alertShownType: 'alert',
          alertShownMessageCode:
            errorType === 'partial-errors'
              ? SOME_HIGH_FIT_ERROR_CODE
              : ALL_HIGH_FIT_ERROR_CODE,
        },
      });
    },
    [pageAnalytics],
  );

  const getHighFitErrorType = useCallback(
    (jobApplications: ApplicationsFitSuccessStatus) => {
      const applicationsFitSummarySuccessStatus: ApplicationsFitSuccessStatus =
        jobApplications.map((application) => ({
          fitSummary: {
            success: application.fitSummary.success,
          },
        }));

      const errorType = getHighFitError(applicationsFitSummarySuccessStatus);

      return errorType;
    },
    [],
  );

  return {
    trackHighFitAlertViewed: trackAlertViewed,
    getHighFitErrorType,
  };
};

/**
 * Get error type if some applications have fit errors or all applications have fit errors.
 * If no applications, return null.
 * @param applications
 * @returns ErrorType | null
 */
const getHighFitError = (
  applications: ApplicationsFitSuccessStatus,
): HighFitErrorType | null => {
  if (applications.length === 0) {
    return null;
  }

  const successCount = applications.filter(
    (app) => app.fitSummary.success === true,
  ).length;

  if (successCount === 0) {
    return 'all-errors';
  }

  if (successCount < applications.length) {
    return 'partial-errors';
  }

  return null;
};
